import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Star } from "lucide-react";
import { Link } from "react-router-dom";
import { fetchServices } from "@/lib/api";

export default function ServicesGrid() {
  const [services, setServices] = useState([]);
  useEffect(() => { fetchServices().then((d) => setServices(d.services)).catch(() => {}); }, []);

  const featured = services.find((s) => s.featured);
  const rest = services.filter((s) => !s.featured);

  return (
    <section data-testid="home-services" className="relative py-8 md:py-12 border-t border-white/5">
      <div className="mx-auto max-w-7xl px-6 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-8">
          <div>
            <div className="text-xs uppercase tracking-[0.24em] text-zinc-500 mb-4">What we run</div>
            <h2 className="font-display font-bold text-4xl md:text-5xl lg:text-6xl tracking-tight max-w-2xl">
              The operating system<br /><span className="italic font-light">for modern music marketing.</span>
            </h2>
          </div>
          <Link to="/services" className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white">
            All services <ArrowUpRight size={16} />
          </Link>
        </div>

        {/* Featured card — full-width, poster-scale, in-house advantage */}
        {featured && (
          <motion.div
            key={featured.slug}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.8 }}
            className="group relative overflow-hidden rounded-3xl border border-white/15 bg-gradient-to-br from-white/[0.06] to-white/[0.01] p-8 md:p-12 mb-6 hover:border-white/30 hover-lift glow-card"
            data-testid={`service-card-${featured.slug}`}
          >
            {/* Subtle diagonal grain */}
            <div
              aria-hidden
              className="absolute inset-0 pointer-events-none opacity-[0.04]"
              style={{
                backgroundImage:
                  "repeating-linear-gradient(135deg, rgba(255,255,255,0.5) 0 1px, transparent 1px 14px)",
              }}
            />
            <div
              aria-hidden
              className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-white/[0.05] blur-3xl pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity"
            />

            <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-14 items-start">
              <div className="lg:col-span-6">
                <div className="flex flex-wrap items-center gap-3 mb-6">
                  <span className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.24em] px-2.5 py-1 border border-white/25 rounded-full text-white/90 bg-white/[0.06]">
                    <Star size={11} className="fill-white/90" /> Flagship
                  </span>
                  {featured.highlight && (
                    <span className="text-[10px] font-mono uppercase tracking-[0.24em] text-zinc-500">
                      · {featured.highlight}
                    </span>
                  )}
                </div>
                <h3 className="font-display font-bold text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[0.98]">
                  {featured.title}
                </h3>
                <p className="mt-6 max-w-md text-lg text-zinc-400 leading-relaxed">
                  {featured.tagline}
                </p>
                <div className="mt-8">
                  <Link
                    to="/services"
                    className="inline-flex items-center gap-2 text-sm text-white/90 border-b border-white/30 pb-0.5 hover:border-white transition-colors"
                  >
                    See how we run it <ArrowUpRight size={14} />
                  </Link>
                </div>
              </div>

              <div className="lg:col-span-6">
                <div className="text-[11px] font-mono uppercase tracking-[0.28em] text-zinc-500 mb-5">
                  What's inside
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                  {featured.items.map((it) => (
                    <div
                      key={it}
                      className="flex items-center gap-2.5 text-sm text-zinc-300 py-2 px-3 rounded-lg border border-white/8 bg-white/[0.02]"
                    >
                      <span className="w-1 h-1 rounded-full bg-white/60 flex-none" />
                      {it}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Rest of the services — original 2-col grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {rest.map((s, i) => {
            // Compute display index — starts after featured
            const idx = featured ? i + 2 : i + 1;
            return (
              <motion.div
                key={s.slug}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.7, delay: i * 0.08 }}
                className="group relative overflow-hidden rounded-3xl border border-white/10 bg-zinc-950/60 p-8 md:p-10 hover:border-white/25 hover-lift glow-card"
                data-testid={`service-card-${s.slug}`}
              >
                <div className="absolute -top-24 -right-24 w-64 h-64 rounded-full bg-white/[0.03] blur-3xl opacity-0 group-hover:opacity-100 transition-opacity" />
                <div className="relative">
                  <div className="flex items-baseline justify-between">
                    <div className="text-xs uppercase tracking-[0.24em] text-zinc-500 font-mono">
                      {String(idx).padStart(2, "0")}
                    </div>
                    {s.coming_soon ? (
                      <span className="text-[10px] font-mono uppercase tracking-[0.24em] px-2.5 py-1 border border-white/25 rounded-full text-white/80" data-testid={`coming-soon-${s.slug}`}>
                        Coming soon
                      </span>
                    ) : (
                      <ArrowUpRight size={20} className="text-zinc-500 group-hover:text-white group-hover:-translate-y-1 group-hover:translate-x-1 transition-all" />
                    )}
                  </div>
                  <h3 className="mt-6 font-display font-semibold text-3xl md:text-4xl tracking-tight">{s.title}</h3>
                  <p className="mt-3 text-zinc-400">{s.tagline}</p>
                  <div className="mt-8 flex flex-wrap gap-2">
                    {s.items.map((it) => (
                      <span key={it} className="text-xs px-3 py-1.5 border border-white/10 rounded-full text-zinc-300">
                        {it}
                      </span>
                    ))}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
